// src/components/SeverityColumn.jsx
// One column of the severity-grouped board.
//
// CLAUDE CODE: prepend `import React from "react";` etc, replace
// the `const PPIcons = window.PPIcons; ...` lines with real imports,
// and add `export default X;` at the bottom (replacing `window.X = X`).

import React, { useState as useState_SC } from "react";
import * as PPIcons from "./_icons";
import { Button } from "./_primitives";
import Card from "./Card";

const PAGE_SIZE = 5;

function SeverityColumn({
  severity,                  // "critical" | "moderate" | "low"
  label,
  posts,
  currentUid,
  isAdmin,
  onUpvote, onSmartTask, onUpdateStatus, onReport, onDelete,
  loading,
}) {
  const [page, setPage] = useState_SC(0);
  const [collapsed, setCollapsed] = useState_SC(false);

  const sortedPosts = [...posts].sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
  const totalPages = Math.max(1, Math.ceil(sortedPosts.length / PAGE_SIZE));
  const slice = sortedPosts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const sevCode = severity === "critical" ? "crit" : severity === "moderate" ? "mod" : "low";
  const sevColor = `var(--pp-${sevCode === "crit" ? "crit" : sevCode === "mod" ? "mod" : "low"})`;

  return (
    <section className="pp-col" data-sev={severity}>
      <header
        className="pp-col-head"
        onClick={() => setCollapsed(c => !c)}
        style={{ cursor: "pointer" }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: 50, background: sevColor, flexShrink: 0,
        }} />
        <span className="pp-col-head-title">{label}</span>
        <span className="pp-col-head-count">{posts.length}</span>
        <button
          aria-label={collapsed ? "Expand" : "Collapse"}
          style={{ border: 0, background: "transparent", padding: 4, cursor: "pointer", color: "var(--text-faint)" }}
        >
          <PPIcons.IconChevD style={{
            width: 14, height: 14,
            transform: collapsed ? "rotate(-90deg)" : "none",
            transition: "transform .15s",
          }} />
        </button>
      </header>

      {!collapsed && (
        <div className="pp-col-body">
          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} className="pp-card" style={{ pointerEvents: "none" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <div className="pp-skel" style={{ width: 56, height: 18 }} />
                  <div className="pp-skel" style={{ width: 64, height: 18 }} />
                </div>
                <div className="pp-skel" style={{ width: "100%", height: 10 }} />
                <div className="pp-skel" style={{ width: "80%", height: 10 }} />
              </div>
            ))
          ) : slice.length === 0 ? (
            <div className="pp-empty" style={{ padding: "32px 8px" }}>
              <span className="pp-empty-icon"><PPIcons.IconCheck /></span>
              <span style={{ fontSize: 12 }}>Nothing here. Nice.</span>
            </div>
          ) : (
            slice.map(p => (
              <Card
                key={p.id}
                post={p}
                currentUid={currentUid}
                isAdmin={isAdmin}
                onUpvote={onUpvote}
                onSmartTask={onSmartTask}
                onUpdateStatus={onUpdateStatus}
                onReport={onReport}
                onDelete={onDelete}
              />
            ))
          )}

          {sortedPosts.length > PAGE_SIZE && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              paddingTop: 8, borderTop: "1px solid var(--divider)", marginTop: 4,
            }}>
              <Button
                variant="ghost" size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                icon={<PPIcons.IconChevL />}
              />
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-faint)" }}>
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="ghost" size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                icon={<PPIcons.IconChevR />}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default SeverityColumn;
